import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDomainCalculation } from '../base/types';
import { EAssessmentSubpage, IOpenCFQQuestionPayload, IOpenWorkoutPayload } from './types';
import { getGtmTime } from './utils';

export interface IAssessmentState {
  started: boolean;
  subpage: EAssessmentSubpage;
  workoutId: number;
  index: number;
  exerciseId: string;
  unityStatus: boolean;
  running: boolean;
  domains: IDomainCalculation[];
  cfqStarted: boolean;
  saving: boolean;
  trainingSchedule: number;
  pushPermission: 0 | 1;
}

export const initialState: IAssessmentState = {
  started: false,
  subpage: EAssessmentSubpage.None,
  workoutId: 0,
  index: 0,
  exerciseId: '',
  unityStatus: false,
  running: false,
  domains: [],
  cfqStarted: false,
  saving: false,
  trainingSchedule: -1,
  pushPermission: 0,
};

export const statePropName = 'assessment';

const assessmentSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    initAssessment(state) {},
    assessmentStarted(state, action: PayloadAction<EAssessmentSubpage>) {
      state.subpage = action.payload;
      state.started = true;
    },
    openWorkout: {
      reducer(state, action: PayloadAction<IOpenWorkoutPayload>) {
        const { workoutId, index, exerciseId } = action.payload;
        state.workoutId = workoutId;
        state.index = index;
        state.exerciseId = exerciseId;
        state.running = false;
        state.unityStatus = false;
      },
      prepare(workoutId: number, index: number, exerciseId: string) {
        return { payload: { workoutId, index, exerciseId } };
      },
    },
    openCFQQuestion: {
      reducer(state, action: PayloadAction<IOpenCFQQuestionPayload>) {
        const { workoutId, index } = action.payload;
        state.started = false;
        state.workoutId = workoutId;
        state.index = index;
      },
      prepare(workoutId: number, index: number) {
        return { payload: { workoutId, index } };
      },
    },
    startCFQ(state) {
      state.cfqStarted = true;
    },
    answerCFQ(state, action: PayloadAction<number>) {
      // No state change needed here
    },
    workoutReady(state) {
      state.unityStatus = true;
    },
    startWorkout(state) {
      state.running = true;
    },
    createTrainingPlanClick(state) {
      state.subpage = EAssessmentSubpage.TrainingSchedule;
    },
    initEvaluationOverview(state) {
      return initialState;
    },
    storeEvaluationResults(state, action: PayloadAction<IDomainCalculation[]>) {
      state.domains = action.payload;
    },
    restartWorkout(state) {
      state.running = false;
      state.unityStatus = false;
    },
    saveFailed(state) {
      state.subpage = EAssessmentSubpage.TrainingSchedule;
    },
    saveSucceed(state) {
      return initialState;
    },
    cycleEvaluation(state, action: PayloadAction<number>) {
      // No state change needed here
    },
    cycleEvaluationComplete(state) {
      // No state change needed here
    },
    selectTrainingSchedule(state, action: PayloadAction<number>) {
      const trainingScheduleGtm = getGtmTime(action.payload);
      state.saving = true;
      state.trainingSchedule = trainingScheduleGtm;
    },
    trainingScheduleSelected(state, action: PayloadAction<0 | 1>) {
      state.saving = false;
      state.pushPermission = action.payload;
      state.subpage = EAssessmentSubpage.SpecialNeeds;
    },
    trainingScheduleSaveFailed(state) {
      state.saving = false;
      state.subpage = EAssessmentSubpage.SpecialNeeds;
    },
    ageGroupSelected: {
      reducer(state, action: PayloadAction<number>) {
        // No state change needed here
      },
      prepare(groupId: number) {
        return { payload: groupId };
      },
    },
    selectSpecialNeeds(state, action: PayloadAction<number>) {
      state.saving = false;
      state.subpage = EAssessmentSubpage.LED;
    },
    trainingLedUpdate(state, action: PayloadAction<0 | 1 | null>) {
      state.saving = true;
    },
    trainingLedActivate(state) {
      // No state change needed here
    },
    trainingLedDeactivate(state) {
      // No state change needed here
    },
    trainingLedSaveFailed(state) {
      state.saving = false;
      state.subpage = EAssessmentSubpage.Intensity;
    },
    trainingLedSaved(state) {
      state.saving = false;
      state.subpage = EAssessmentSubpage.Intensity;
    },
  },
});

export const AssessmentActions = assessmentSlice.actions;
export default assessmentSlice.reducer;
